import React, { useState, useEffect } from "react";
import { ComponentRenderer } from "../../ComponentRenderer";
import { SettingsPopup } from "./SettingsPopup";
import {
  DrawerMenuConfig,
  ComponentConfig,
  LayoutTypes,
  ComponentType,
  HeightMode,
} from "../../../services/types/ComponentTypes";

interface SideMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onMenuItemClick?: (action: string) => void;
  activeView?: string;
  drawerMenuConfig?: DrawerMenuConfig;
}

export const SideMenu: React.FC<SideMenuProps> = ({
  isOpen,
  onToggle,
  onMenuItemClick,
  activeView = "dashboard",
  drawerMenuConfig,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Convert drawer menu config to component config format
  const convertDrawerMenuToConfig = (
    drawerMenu?: DrawerMenuConfig
  ): ComponentConfig[] => {
    if (!drawerMenu) {
      return [];
    }

    const menuItems: ComponentConfig[] = drawerMenu.menu.map((menuItem) => {
      // If the menu item has a submenu, create a CollapsibleNavItem
      if (menuItem.submenu) {
        return {
          componentType: ComponentType.COLLAPSIBLE_NAV_ITEM,
          name: menuItem.title.toLowerCase().replace(/\s+/g, "_"),
          label: menuItem.title,
          icon: menuItem.iconPath || "📄",
          tooltip: menuItem.title,
          defaultExpanded: false,
          submenuMode: menuItem.submenu.mode || "inline", // Pass the mode from config
          submenuBackgroundColor: menuItem.submenu.backgroundColor,
          submenuTextColor: menuItem.submenu.contentTextColor,
          style: {
            padding: 8,
            textColor:
              menuItem.contentTextColor ||
              drawerMenu.contentTextColor ||
              "#FFFFFF",
            backgroundColor: "transparent",
            margin: 2,
          },
          submenuItems: menuItem.submenu.items.map((subItem) => ({
            componentType: ComponentType.NAV_ITEM,
            name:
              subItem.screenId ||
              subItem.title.toLowerCase().replace(/\s+/g, "_"),
            label: subItem.title,
            icon: subItem.iconPath || "📄",
            route: `/screen/${subItem.screenId ||
              subItem.title.toLowerCase().replace(/\s+/g, "_")
              }`,
            tooltip: subItem.title,
            onPressAction: `navigate_${subItem.screenId ||
              subItem.title.toLowerCase().replace(/\s+/g, "_")
              }`,
            style: {
              padding: 8,
              textColor: menuItem.submenu?.contentTextColor || "#FFFFFF",
              backgroundColor: "transparent",
              margin: 1,
            },
          })),
        } as any;
      } else {
        // Create a simple NavItem
        return {
          componentType: ComponentType.NAV_ITEM,
          name:
            menuItem.screenId ||
            menuItem.title.toLowerCase().replace(/\s+/g, "_"),
          label: menuItem.title,
          icon: menuItem.iconPath || "📄",
          route: `/screen/${menuItem.screenId ||
            menuItem.title.toLowerCase().replace(/\s+/g, "_")
            }`,
          tooltip: menuItem.title,
          onPressAction: `navigate_${menuItem.screenId ||
            menuItem.title.toLowerCase().replace(/\s+/g, "_")
            }`,
          style: {
            padding: 8,
            textColor:
              menuItem.contentTextColor ||
              drawerMenu.contentTextColor ||
              "#FFFFFF",
            backgroundColor: "transparent",
            margin: 2,
          },
        } as any;
      }
    });

    return [
      {
        componentType: LayoutTypes.GROUP,
        style: {
          // backgroundColor removed - handled by CSS class side-menu-group
          padding: 0,
          heightMode: HeightMode.FULL,
        },
        childComponents: [
          {
            componentType: LayoutTypes.GRID,
            columnNumber: "w1",
            style: {
              backgroundColor: "transparent",
            },
            childComponents: menuItems,
          } as any,
        ],
      } as any,
    ];
  };

  const sideMenuConfig = convertDrawerMenuToConfig(drawerMenuConfig);

  // Check screen size and set responsive states
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024;

      setIsMobile(mobile);
      setIsTablet(tablet);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Handle body overflow for mobile and tablet menu
  useEffect(() => {
    if (isMobile || isTablet) {
      if (isOpen) {
        document.body.classList.add("menu-open");
      } else {
        document.body.classList.remove("menu-open");
      }
    } else {
      // Always remove on desktop
      document.body.classList.remove("menu-open");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("menu-open");
    };
  }, [isOpen, isMobile, isTablet]);

  const handleToggleExpanded = (itemName: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set<string>();
      // If the item is already expanded, close it (empty set)
      // Otherwise, open only this item (close all others)
      if (!prev.has(itemName)) {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  const handleActionTrigger = (actionId: string, data?: any) => {
    console.log("SideMenu - Action triggered:", actionId, data);

    if (actionId === "toggle_sidebar") {
      onToggle();
      return;
    }

    // Handle navigation actions
    // If action is already in navigate_* format, use it directly
    if (actionId && actionId.startsWith("navigate_")) {
      console.log("SideMenu - Forwarding navigation action:", actionId);
      if (onMenuItemClick) {
        onMenuItemClick(actionId);
      }
    } else if (actionId === "navigate" && data?.route) {
      // For generic "navigate" action with route, try to map or extract
      const routeMap: Record<string, string> = {
        "/dashboard": "navigate_dashboard",
        "/garage": "navigate_garage",
        "/garage/vehicles": "navigate_vehicles",
        "/garage/services": "navigate_services",
        "/garage/maintenance": "navigate_maintenance",
        "/garage/parts": "navigate_parts",
        "/garage/inspection": "navigate_inspection",
        "/inventory": "navigate_inventory",
        "/sales": "navigate_sales",
        "/sales/orders": "navigate_sales_orders",
        "/sales/invoices": "navigate_sales_invoices",
        "/sales/quotations": "navigate_sales_quotations",
        "/purchases": "navigate_purchases",
        "/purchases/orders": "navigate_modulev1/purchase_order_summary",
        "/purchases/invoices": "navigate_purchase_invoices",
        "/purchases/vendors": "navigate_modulev1/vendor_summary",
        "/purchases/return-vouchers":
          "navigate_modulev1/purchase_return_summary",
        "/accounts": "navigate_accounts",
        "/accounts/cash-and-bank": "navigate_cash_and_bank",
        "/accounts/charts-of-accounts": "navigate_charts_of_accounts",
        "/vouchers": "navigate_vouchers",
        "/employee": "navigate_employee",
        "/master": "navigate_master",
        "/master/customers": "navigate_customers",
        "/master/products": "navigate_products",
        "/master/categories": "navigate_categories",
        "/master/units": "navigate_units",
        "/assets-liabilities": "navigate_assets_liabilities",
        "/reports": "navigate_reports",
      };

      const mappedAction = routeMap[data.route];
      if (mappedAction && onMenuItemClick) {
        onMenuItemClick(mappedAction);
      }
    }

    // Close menu on mobile and tablet when item is clicked
    if (isMobile || isTablet) {
      onToggle();
    }
  };

  const getMenuWidth = () => {
    if (isMobile) return "280px";
    if (isTablet) return "260px";
    return "200px";
  };

  return (
    <>
      {/* Overlay for mobile and tablet */}
      <div
        className={`fixed top-16  left-0 w-screen h-[calc(100vh-4rem)] bg-black/50 z-[998] transition-opacity duration-300 ${(isMobile || isTablet) && isOpen
          ? "block opacity-100"
          : "hidden opacity-0"
          }`}
        onClick={onToggle}
        onTouchStart={(e) => e.preventDefault()} // Prevent scroll behind overlay
      />

      {/* Side Menu */}
      <div
        className={`fixed top-16 left-0 h-[calc(100vh-4rem)] bg-gray-800 dark:bg-gray-900 text-white flex flex-col shadow-xl border-r border-gray-300 dark:border-gray-700 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] z-[999] ${isOpen
          ? "translate-x-0"
          : "-translate-x-full"
          }`}
        style={{ width: getMenuWidth() }}
        data-tour="side-menu"
      >

        {/* Close button for mobile and tablet */}
        <button
          className={`absolute top-2 right-2 bg-white/10 border-none text-white text-sm cursor-pointer p-1.5 rounded-md flex items-center justify-center w-7 h-7 z-[1001] transition-colors duration-200 hover:bg-white/20 ${isMobile || isTablet ? "flex" : "hidden"
            }`}
          onClick={onToggle}
          aria-label="Close menu"
        >
          ✕
        </button>

        {/* Scrollable Content Area */}
        <nav className="sidebar-scroll flex-1 overflow-y-auto py-2 space-y-0.5">
          {/* Render the sidebar using ComponentRenderer */}
          <div className="sidemenugrid side-menu-group">
          <ComponentRenderer
            config={sideMenuConfig}
            onActionTrigger={handleActionTrigger}
            expandedItems={expandedItems}
            onToggleExpanded={handleToggleExpanded}
            activeView={activeView}
          />
          </div>
        </nav>

        {/* Settings Footer */}
        <div className="p-4 border-t border-white/10 shrink-0">
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="w-full flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white/10 transition-colors duration-200 group"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 group-hover:rotate-90 transition-transform duration-500"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.74v-.47a2 2 0 0 1 1-1.74l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>
            <span className="font-medium">Settings</span>
          </button>
        </div>
      </div>

      <SettingsPopup
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  );
};
